import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Card, CircularProgress, Stack, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import { useGetFocusesDictionaryQuery } from '../../../../api'
import { DictionaryMultiSelect } from '../../../../components/ui-kit/DictionaryMultiSelect'
import { FormBlock } from '../../../../common/FormGenerator'
import { getFormConfig } from './settingsConfig'
import {
  teamFormSchema,
  teamFormToPayload,
  teamToFormValues,
  type TeamFormValues,
} from '../../CreateTeam/ZodValidationSchema'
import { CardStyle, TypStyle } from './style'
import { useGetTeamQuery, useUpdateTeamMutation, useUploadTeamCoverMutation } from '../../../../api/teams'
import { extractApiErrorMessage } from '../../../../utils/api'
import { isTeamFormUnchanged } from '../../../../utils/team/areTeamFormOutputsEqual'

export const EditTeamForm = () => {
  const { id } = useParams<{ id: string }>()
  const teamId = id ? parseInt(id, 10) : NaN
  const { data: initialData } = useGetTeamQuery({ id: teamId }, { skip: Number.isNaN(teamId) })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [updateTeam] = useUpdateTeamMutation()
  const [uploadTeamCover] = useUploadTeamCoverMutation()
  const qFocuses = useGetFocusesDictionaryQuery()
  const baselineRef = useRef<TeamFormValues | null>(null)

  const methods = useForm({
    mode: 'onBlur',
    resolver: zodResolver(teamFormSchema),
    defaultValues: {
      title: '',
      city: '',
      status: '',
      descriptionShort: '',
      region: '',
      image: '',
      directionIds: [] as number[],
      goalsText: '',
      resourcesText: '',
      needsText: '',
    },
  })

  const { handleSubmit, reset, getValues } = methods

  useEffect(() => {
    if (!initialData || !qFocuses.isSuccess || !qFocuses.data) {
      return
    }
    const values = teamToFormValues(initialData, qFocuses.data)
    reset(values)
    baselineRef.current = values
  }, [initialData, qFocuses.isSuccess, qFocuses.data, reset])

  const onSubmit = async (data: TeamFormValues) => {
    if (Number.isNaN(teamId) || !qFocuses.data || !initialData) {
      return
    }

    if (baselineRef.current != null && isTeamFormUnchanged(data, baselineRef.current)) {
      toast.info('Нет изменений')
      return
    }

    setIsSubmitting(true)
    try {
      await uploadCoverIfNeeded()
      const payload = teamFormToPayload(data, qFocuses.data)
      payload.descriptionFull = initialData.descriptionFull ?? ''
      await updateTeam({ id: teamId, payload }).unwrap()
      baselineRef.current = data
      toast.success('Команда сохранена')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить изменения')
    } finally {
      setIsSubmitting(false)
    }
  }

  const uploadCoverIfNeeded = async () => {
    if (Number.isNaN(teamId)) {
      return
    }

    const image = getValues('image')
    if (image instanceof File) {
      await uploadTeamCover({ teamId, file: image }).unwrap()
    }
  }

  if (!initialData) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (!qFocuses.isError && !qFocuses.isSuccess) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (qFocuses.isError || !qFocuses.data) {
    return (
      <Typography color="error" textAlign="center" px={2}>
        Не удалось загрузить справочники. Обновите страницу.
      </Typography>
    )
  }

  return (
    <FormProvider {...methods}>
      <Stack gap={2} width="100%" component="form" onSubmit={handleSubmit(onSubmit)}>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Основные
            </Typography>
            <Stack gap={1.5}>
              <FormBlock formFieldConfig={getFormConfig('image')} />
              <FormBlock formFieldConfig={getFormConfig('title')} />
              <FormBlock formFieldConfig={getFormConfig('city')} />
              <FormBlock formFieldConfig={getFormConfig('status')} />
              <FormBlock formFieldConfig={getFormConfig('descriptionShort')} />
              <FormBlock formFieldConfig={getFormConfig('region')} />
            </Stack>
          </Card>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Направления
            </Typography>
            <DictionaryMultiSelect<TeamFormValues>
              name="directionIds"
              dictionary={qFocuses.data}
              placeholder="Выберите из списка"
            />
          </Card>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Цели
            </Typography>
            <FormBlock formFieldConfig={getFormConfig('goalsText')} />
          </Card>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Ресурсы
            </Typography>
            <FormBlock formFieldConfig={getFormConfig('resourcesText')} />
          </Card>
          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Потребности
            </Typography>
            <FormBlock formFieldConfig={getFormConfig('needsText')} />
          </Card>

          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="action" disabled={isSubmitting}>
              {isSubmitting ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} color="inherit" />
                  <span>Сохранение...</span>
                </Stack>
              ) : (
                'Сохранить'
              )}
            </Button>
          </Stack>
      </Stack>
    </FormProvider>
  )
}
